import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, tokenStore, extractError } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    // status: 'loading' | 'authed' | 'guest'
    const [status, setStatus] = useState("loading");
    const [user, setUser] = useState(null);

    const loadMe = useCallback(async () => {
        if (!tokenStore.get()) {
            setStatus("guest");
            setUser(null);
            return;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            setStatus("authed");
        } catch (e) {
            tokenStore.clear();
            setUser(null);
            setStatus("guest");
        }
    }, []);

    useEffect(() => {
        loadMe();
    }, [loadMe]);

    const login = async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            tokenStore.set(data.access_token);
            setUser(data.user);
            setStatus("authed");
            return { ok: true, user: data.user };
        } catch (e) {
            return { ok: false, error: extractError(e, "Invalid email or password") };
        }
    };

    const acceptInvitation = async (token, password, full_name) => {
        try {
            const { data } = await api.post("/auth/accept-invitation", {
                token,
                password,
                full_name,
            });
            tokenStore.set(data.access_token);
            setUser(data.user);
            setStatus("authed");
            return { ok: true, user: data.user };
        } catch (e) {
            return { ok: false, error: extractError(e, "Could not accept invitation") };
        }
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch (e) {
            // ignore
        }
        tokenStore.clear();
        setUser(null);
        setStatus("guest");
    };

    return (
        <AuthContext.Provider
            value={{ status, user, login, logout, acceptInvitation, refresh: loadMe }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
    return ctx;
}
